// Libs
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { useTheme } from '@material-ui/core/styles';

// Components
import Box from '@material-ui/core/Box';
import ButtonBase from '@material-ui/core/ButtonBase';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import ResultIcon from 'COMPONENTS/ResultIcon';
import SignalCellularAltIcon from '@material-ui/icons/SignalCellularAlt';

// Actions
import { goToCheck, leaveClick } from 'REDUCERS/navSlice';

// Utils
import { checkList } from 'SRC/store';
import { CHECK_ICON_MAP, CHECK_RESULT_MAP, CHECK_TRANSLATION_MAP } from 'CHECKS';
import { INFORMATION_LEVEL, NARROW_LAYOUT, SUCCESS_LEVEL, UNKNOWN_LEVEL } from 'UTILS/constants';
import useTranslator from 'UTILS/hooks/useTranslator';
import { onTrackedClick } from 'UTILS/callbacks';
import translations from './locales.json';

const Summary = () => {
  const [translate] = useTranslator(translations);
  const theme = useTheme();
  const dispatch = useDispatch();
  const greyColor = theme.palette.grey;
  const checks = useSelector(state => state.checks);
  const isSmallScreen = useSelector(state => state.app.layout === NARROW_LAYOUT);
  const shouldShowBackButton = useSelector(state => state.app.backUrl || state.app.closeOnBack);
  const containerClasses = cx('summary-container', {
    'summary-container_largeScreen': !isSmallScreen,
    'summary-container_noBackButton': !shouldShowBackButton,
  });

  const handleCheckClick = (checkLabel) => {
    onTrackedClick(`navigate_${checkLabel}`, 'summary');
    dispatch(goToCheck({ checkLabel }));
  };

  const handlePreviousClick = () => {
    onTrackedClick('previous', 'summary');
    dispatch(goToCheck({ checkLabel: checkList[checkList.length - 1] }));
  };

  const handleFinishClick = () => {
    onTrackedClick('back', 'summary');
    dispatch(leaveClick());
  };

  const mainLevel = Object.values(checks).reduce((mainLevel, { level }) => level === UNKNOWN_LEVEL ? UNKNOWN_LEVEL : Math.max(level, mainLevel), UNKNOWN_LEVEL);

  return (
    <Box className={containerClasses} >
      <Box bgcolor={theme.palette.background.paper} className='summary-surface' >
        <Box className='summary-title' >
          <SignalCellularAltIcon className='summary-iconTitle' />
          <Typography variant='subtitle2' component='span'>{translate('checkSummary')}</Typography>
        </Box>
        <Box className='summary-content'>
          <Box className='summary-conclusionMessage'>
            {mainLevel > INFORMATION_LEVEL ? (
              <Typography variant={isSmallScreen ? 'body2' : 'body1'} >{translate('somethingInYourTestHasNotBeenValidated-makeSureEverythingIsWorkingBeforeYouFinishYourTest')}</Typography>
            ) : mainLevel > SUCCESS_LEVEL ? (
              <Typography variant={isSmallScreen ? 'body2' : 'body1'} >{translate('theTestIsSuccessfulButMightBeImproved-youCanProceedAnywayIfYouWant')}</Typography>
            ) : (
              <Typography variant={isSmallScreen ? 'body2' : 'body1'} >{translate('allTheElementsNecessaryForYourTeleconsultationHaveBeenSuccessfullyValidatedYouCanNowCompleteTheTest')}</Typography>
            )}
          </Box>
          <Box className='summary-checkList'>
            {checkList.map(check => {
              const { level, resultList } = checks[check];
              const Icon = CHECK_ICON_MAP[check];
              const [translateResults] = useTranslator(CHECK_TRANSLATION_MAP[check]);
              const currentWorstResult = resultList.reduce((currentWorstResult, currentResult) => {
                return (currentWorstResult.type > currentResult.type) ? currentWorstResult : currentResult;
              });
              const { type } = currentWorstResult;
              const { message } = CHECK_RESULT_MAP[type];
              return (
                <ButtonBase key={check} style={{ margin: '8px 24px 0' }} onClick={() => handleCheckClick(check)}>
                  <Box className='summary-checkResultContainer' bgcolor={greyColor[100]} >
                    <Icon className='summary-iconCheck' />
                    <Typography className='summary-message' variant={isSmallScreen ? 'body2' : 'body1'} >{translateResults(message)}</Typography>
                    <ResultIcon resultLevel={level} />
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
        <Box className='summary-footer'>
          <Button color='primary' onClick={handlePreviousClick}>{translate('previous')}</Button>
          {shouldShowBackButton && (
            <Button color='primary' variant='contained' onClick={handleFinishClick}>{translate('finish')}</Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Summary;
